import { useState } from "react"
import { IconLoader2, IconLock } from "@tabler/icons-react"
import { toast } from "sonner"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { type BookingSeasonSnapshot, useCloseBookingSeasonNowMutation } from "../bookingSeasonApi"
import { closedReasonLabel, formatSeasonDateTime } from "../bookingSeasonFormat"

interface SeasonStatusCardProps {
  season: BookingSeasonSnapshot
  /** Called with the season as it stands after closing, so the form can follow it. */
  onClosed: (season: BookingSeasonSnapshot) => void
}

/** Whether the public can book right now, why not, and who last changed it. */
export function SeasonStatusCard({ season, onClosed }: SeasonStatusCardProps) {
  const [confirming, setConfirming] = useState(false)
  const [closeNow, { isLoading: closing }] = useCloseBookingSeasonNowMutation()

  const isOpen = season.status === "Open"
  const reason = closedReasonLabel(season)

  async function handleCloseNow() {
    try {
      const result = await closeNow().unwrap()
      onClosed(result)
      toast.success("Booking closed.", {
        description: "The public website and phone line are no longer taking new appointments.",
      })
    } catch (err) {
      const status = (err as { status?: number }).status
      toast.error(
        status === 403
          ? "You do not have permission to close booking."
          : "Could not close booking. Try again in a moment.",
      )
    } finally {
      setConfirming(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          Public booking is
          <Badge variant={isOpen ? "default" : "destructive"}>{season.status}</Badge>
        </CardTitle>
        <CardDescription>
          {isOpen
            ? "The public website and phone line can book new appointments."
            : "The public website and phone line cannot book new appointments. Staff can still book, and anyone can manage an existing appointment."}
        </CardDescription>
        {isOpen && (
          <CardAction>
            <Button variant="destructive" size="sm" onClick={() => setConfirming(true)} disabled={closing}>
              <IconLock className="size-4" />
              Close booking now
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-[max-content_1fr]">
          {reason && (
            <>
              <dt className="text-muted-foreground">Reason</dt>
              <dd className="font-medium">{reason}</dd>
            </>
          )}
          {season.opensAt && season.closesAt && (
            <>
              <dt className="text-muted-foreground">Booking opens</dt>
              <dd>{formatSeasonDateTime(season.opensAt)}</dd>
              <dt className="text-muted-foreground">Booking closes</dt>
              <dd>{formatSeasonDateTime(season.closesAt)}</dd>
            </>
          )}
        </dl>
        {season.createdBy && season.createdAt && (
          <p className="mt-4 text-xs text-muted-foreground">
            Last changed by {season.createdBy} on {formatSeasonDateTime(season.createdAt)}
          </p>
        )}
      </CardContent>

      <AlertDialog open={confirming} onOpenChange={(open) => !open && !closing && setConfirming(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close booking now?</AlertDialogTitle>
            <AlertDialogDescription>
              Public website and phone callers will immediately stop being able to book new appointments. Existing
              appointments are not affected. Staff can still book.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={closing}>Keep booking open</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={(e) => {
                e.preventDefault()
                void handleCloseNow()
              }}
              disabled={closing}
            >
              {closing && <IconLoader2 className="size-4 animate-spin" />}
              Close booking now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
